// Lista unica per annunci (bacheca) e comunicazioni: cambia solo la config
// (servizi, chiave delle views, testi), vedi boardListConfigs.js.
import { useMemo, useState } from 'react';
import { Heading, Button, Pane, Checkbox, majorScale, toaster } from 'evergreen-ui';
import BoardListCard from './BoardListCard';
import useReadIds from './useReadIds';
import useViewerDetails from './useViewerDetails';
import '../../styles/responsive.css';

export const sortByDate = (list, field, order) => {
    const direction = order === 'asc' ? 1 : -1;
    return [...list].sort((a, b) => direction * (new Date(a[field]) - new Date(b[field])));
};

const arrow = (sort, field) => (sort.field === field ? (sort.order === 'asc' ? '↑' : '↓') : '');

const SortButtons = ({ sort, onSort }) => (
    <Pane marginBottom={majorScale(2)} className="responsive-button-group" display="flex" gap={16} alignItems="center">
        <Button appearance="primary" intent="success" onClick={() => onSort('timestamp')}>
            Ordina per data di creazione {arrow(sort, 'timestamp')}
        </Button>
        <Button appearance="primary" intent="success" onClick={() => onSort('last_modified')}>
            Ordina per data di modifica {arrow(sort, 'last_modified')}
        </Button>
    </Pane>
);

// Azioni per id, non per posizione: la lista a video è ordinata in modo diverso dall'array.
const createBoardActions = ({ config, setItems, userId, readState }) => ({
    onRemove: async (item) => {
        if (!window.confirm(config.labels.confirmRemove(item.title))) return;
        try {
            await config.remove(item.id);
            setItems((prev) => prev.filter((other) => other.id !== item.id));
            toaster.notify(config.labels.removed);
        } catch (error) {
            console.error(config.labels.removeError, error);
            toaster.danger(config.labels.removeError);
        }
    },
    onMarkAsRead: async (id) => {
        if (readState.readIds.has(id)) return;
        try {
            await config.markAsRead(id, userId);
            readState.addRead(id);
            toaster.success(config.labels.markedRead);
        } catch (error) {
            console.error(config.labels.markReadError, error);
            toaster.danger(config.labels.markReadError);
        }
    },
});

const BoardList = ({ config, items, views, setItems, onEdit, userId, userRole }) => {
    const [hideRead, setHideRead] = useState(false);
    const [sort, setSort] = useState({ field: 'timestamp', order: 'desc' });
    const [openId, setOpenId] = useState(null);
    const readState = useReadIds(views, userId, config.viewKey);
    const viewerDetails = useViewerDetails(views);
    const actions = createBoardActions({ config, setItems, userId, readState });

    const visibleItems = useMemo(() => {
        const list = Array.isArray(items) ? items : [];
        const unread = hideRead ? list.filter((item) => !readState.readIds.has(item.id)) : list;
        return sortByDate(unread, sort.field, sort.order);
    }, [items, hideRead, readState.readIds, sort]);

    const onSort = (field) => setSort((prev) => ({ field, order: prev.order === 'asc' ? 'desc' : 'asc' }));
    const ctx = {
        ...actions, userRole, views, viewerDetails, viewKey: config.viewKey, labels: config.labels,
        readIds: readState.readIds, onEdit,
        onToggle: (item) => setOpenId((prev) => (prev === item.id ? null : item.id)),
    };

    return (
        <Pane>
            <Heading size={600} marginBottom={16}>{config.labels.heading}</Heading>
            {(userRole === 'operatore' || userRole === 'educatore') && (
                <Pane marginBottom={majorScale(2)}>
                    <Checkbox label={config.labels.hideRead} checked={hideRead} onChange={(e) => setHideRead(e.target.checked)} />
                </Pane>
            )}
            <SortButtons sort={sort} onSort={onSort} />
            {visibleItems.map((item) => (
                <BoardListCard key={item.id} item={item} isOpen={openId === item.id} ctx={ctx} />
            ))}
        </Pane>
    );
};

export default BoardList;
